import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import AutocompleteCFormik from './AutocompleteCFormik';
import { getCompaniesForAreaSelect } from 'src/services/getCompaniesForAreaSelect';
import { mapCompaniesToAreaOptions } from 'src/utils/mappers/eventForm/area.mapper';

const SEARCH_DEBOUNCE_MS = 300;

const AutocompleteCAreas = ({
	options: staticOptions = [],
	excludeIds = [],
	...props
}) => {
	const useServerSearch = staticOptions.length === 0;
	const [options, setOptions] = useState(staticOptions);
	const [loading, setLoading] = useState(false);
	const [inputValue, setInputValue] = useState('');
	const [isOpen, setIsOpen] = useState(false);
	const requestIdRef = useRef(0);
	const excludeKey = useMemo(
		() => excludeIds.map(String).join(','),
		[excludeIds],
	);

	const loadOptions = useCallback(
		async (search = '') => {
			if (!useServerSearch) return;

			const requestId = ++requestIdRef.current;

			setLoading(true);

			try {
				const companies = await getCompaniesForAreaSelect({ search });
				const nextOptions = mapCompaniesToAreaOptions(companies, {
					excludeIds,
				});

				if (requestId === requestIdRef.current) {
					setOptions(nextOptions);
				}
			} catch {
				if (requestId === requestIdRef.current) {
					setOptions([]);
				}
			} finally {
				if (requestId === requestIdRef.current) {
					setLoading(false);
				}
			}
		},
		[excludeIds, useServerSearch],
	);

	useEffect(() => {
		if (!useServerSearch) {
			setOptions(staticOptions);
		}
	}, [staticOptions, useServerSearch]);

	useEffect(() => {
		if (!useServerSearch || !isOpen) return undefined;

		const timer = setTimeout(() => {
			loadOptions(inputValue);
		}, SEARCH_DEBOUNCE_MS);

		return () => clearTimeout(timer);
	}, [excludeKey, inputValue, isOpen, loadOptions, useServerSearch]);

	const handleInputChange = useCallback((_event, value, reason) => {
		if (reason === 'input' || reason === 'clear') {
			setInputValue(value);
		}
	}, []);

	const handleOpen = useCallback(() => {
		setIsOpen(true);
	}, []);

	const handleClose = useCallback(() => {
		setIsOpen(false);
	}, []);

	return (
		<AutocompleteCFormik
			options={options}
			loading={loading}
			filterOptions={useServerSearch ? (all) => all : undefined}
			onInputChange={useServerSearch ? handleInputChange : undefined}
			onOpen={useServerSearch ? handleOpen : undefined}
			onClose={useServerSearch ? handleClose : undefined}
			{...props}
		/>
	);
};

AutocompleteCAreas.propTypes = {
	options: PropTypes.array,
	excludeIds: PropTypes.arrayOf(
		PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	),
};

export default AutocompleteCAreas;
